import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AccountingWrapper from "../../../../Components/v2/Accounting/AccountingWrapper";
import TableActions from "../../../../Components/v2/Table/TableActions";
import Table from "../../../../Components/v2/Table";
import AccountingPageHeader from "../../../../Components/v2/Accounting/AccountingPageHeader";
import {
  addLocalNotification,
  clearAccountBalanceData,
  clearAccountingAccount,
  clearAcctAccountError,
  clearDocumentExcelAccountError,
  requestDeleteAccount,
  requestGetAccountBalance,
  requestListAccount,
  useAccountingDispatch,
  useAccountingState,
  useAuthState,
  useNotificationDispatch,
} from "../../../../Context";
import Pagination from "../../../../Components/v2/Pagination";
import {
  cancelAPIRequest,
  cancelStorageRequest,
  convertToTitleCase,
  formatCurrency,
  getSafeInternalPath,
} from "../../../../Utils";
import { useTranslation } from "react-i18next";
import ChartOfAccountModal from "../../../../Components/v2/Accounting/ChartOfAccountModal";
import { ModalAlert } from "../../../../Components/ModalAlert";
import { PlusIcon } from "@heroicons/react/outline";
import AccountUploadModal from "../../../../Components/v2/Accounting/AccountUploadModal";
import Button from "../../../../Components/v2/Button";
import FormError from "../../../../Components/v2/Form/FormError";
import ErrorDialog from "../../../../Components/v2/ErrorDialog";

const ChartOfAccounts = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [accountModalOpen, setAccountModalOpen] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isAccountUploadModalOpen, setIsAccountUploadModalOpen] =
    useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [initialAccountValues, setInitialAccountValues] = useState(null);
  const [returnTo, setReturnTo] = useState(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(5);

  const dispatch = useAccountingDispatch();
  const notificationDispatch = useNotificationDispatch();

  const {
    storageRequest,
    request,
    accounts,
    accountLoading,
    accountError,
    accountNextTokens,
    accountID,
    deletedID,
    deleteLoading,
    deleteError,
    accountBalances,
    accountBalancesLoading,
    accountBalancesError,
  } = useAccountingState();
  const { user, loading: userLoading } = useAuthState();

  const tableLoading = accountLoading || userLoading;

  // Auto-open create modal when redirected from opening balance page
  useEffect(() => {
    if (searchParams.get("create") === "true") {
      const name = searchParams.get("name") || "";
      const code = searchParams.get("code") || "";
      const category = searchParams.get("category") || "";
      // Query-controlled, so only an in-app path is ever accepted here.
      const rt = getSafeInternalPath(searchParams.get("returnTo"));
      setInitialAccountValues({ name, code, category });
      setReturnTo(rt);
      setSelectedAccount(null);
      setAccountModalOpen(true);
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Navigate back after account creation when returnTo is set
  useEffect(() => {
    if (accountID && returnTo) {
      navigate(returnTo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountID]);

  const loadAccountBalances = () => {
    if (user?.mainBusiness?.id && accounts.length > 0) {
      requestGetAccountBalance(dispatch, {
        businessID: user.mainBusiness.id,
        accountIDs: accounts.map((a) => a.id),
      });
    }
  };

  // Fetch account balances after accounts are loaded
  useEffect(() => {
    loadAccountBalances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts]);

  // Clear errors on unmount
  useEffect(() => {
    return () => {
      if (request) {
        cancelAPIRequest(request);
      }
      if (storageRequest) {
        cancelStorageRequest(storageRequest);
      }
      clearAccountingAccount(dispatch);
      clearAcctAccountError(dispatch);
      clearAccountBalanceData(dispatch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user?.mainBusiness?.id) {
      requestListAccount(dispatch, {
        page: 1,
        limit: perPage,
        businessID: user.mainBusiness.id,
      });
    }
  }, [dispatch, perPage, user?.mainBusiness?.id]);

  //  Handle delete success
  useEffect(() => {
    if (deletedID) {
      // Close modal
      setShowConfirmModal(false);
      setSelectedAccount(null);
      // Show success notification
      addLocalNotification(notificationDispatch, {
        id: `delete-account-${deletedID}`,
        type: "success",
        title: "common.success",
        body: "accounts.notifications.deleteSuccess",
        showFull: true,
      });
    }
  }, [notificationDispatch, deletedID]);

  useEffect(() => {
    if (deleteError) {
      setShowConfirmModal(false);
      setShowErrorModal(true);
    }
  }, [deleteError]);

  const onPageChange = (selectedPage) => {
    setPage(selectedPage);
    if (accountNextTokens[selectedPage] || selectedPage === 1) {
      requestListAccount(dispatch, {
        page: selectedPage,
        limit: perPage,
        businessID: user?.mainBusiness?.id,
        nextTokens: accountNextTokens[selectedPage],
      });
    }
  };

  const onPerPageChange = (itemsPerPage) => {
    setPerPage(itemsPerPage);
  };

  const onDelete = () => {
    requestDeleteAccount(dispatch, {
      id: selectedAccount?.id,
    });
  };

  const handleRemove = (account) => {
    setSelectedAccount(account);
    setShowConfirmModal(true);
  };

  const getDeleteDescription = () => {
    if (!selectedAccount) return null;
    return t("accounts.modal.description.deleteItem", {
      name: selectedAccount.name,
      code: selectedAccount.code,
      category: selectedAccount.category
        ? t(convertToTitleCase(selectedAccount.category))
        : "-",
      interpolation: { escapeValue: false },
    });
  };

  const handleEdit = (account) => {
    setSelectedAccount(account);
    setAccountModalOpen(true);
  };

  const handleAdd = () => {
    setSelectedAccount(null);
    setAccountModalOpen(true);
  };

  const handleClose = () => {
    cancelAPIRequest(request);
    cancelStorageRequest(storageRequest);
    setAccountModalOpen(false);
    setSelectedAccount(null);
    setInitialAccountValues(null);
    setReturnTo(null);
    setIsAccountUploadModalOpen(false);
    clearDocumentExcelAccountError(dispatch);
  };

  // Only filter by balances that belong to the rows on screen. A late or failed
  // balance response for another page would otherwise hide every row.
  const balancesMatchPage = accounts.some((a) => accountBalances[a.id]);
  const displayedAccounts =
    !accountBalancesLoading && balancesMatchPage
      ? accounts.filter((a) => accountBalances[a.id])
      : accounts;

  const reloadAccounts = () => {
    if (!user?.mainBusiness?.id) return;
    requestListAccount(dispatch, {
      page,
      limit: perPage,
      businessID: user.mainBusiness.id,
      nextTokens: accountNextTokens[page],
    });
  };

  const dropDownItems = useMemo(
    () => [
      {
        name: t("accounts.action.upload"),
        onClick: () => {
          setIsAccountUploadModalOpen(true);
          clearDocumentExcelAccountError(dispatch);
        },
      },
    ],
    [t, dispatch],
  );

  return (
    <AccountingWrapper>
      <main className="lg:col-span-9 min-w-0">
        {/* Page Header */}
        <AccountingPageHeader
          title="accounting.coa"
          dropDownAction={{
            label: t("accounts.header.title.add"),
            onClick: handleAdd,
            icon: <PlusIcon className="h-5 w-5" />,
            items: dropDownItems,
          }}
          additionalActions={
            <Button
              type="button"
              variant="secondary"
              color="blue"
              className="w-full sm:w-auto"
              onClick={() => navigate("/v2/accounting/coa/opening-balance")}
            >
              {t("Opening Balance")}
            </Button>
          }
        />

        {/* CoA Table */}
        <div className="px-4 py-5 sm:px-6 lg:px-8">
          <FormError
            className="mb-4"
            error={!accountError && accountBalancesError}
            title="accounts.errors.balances"
            onRetry={loadAccountBalances}
          />
          <Table
            isLoading={tableLoading}
            error={accountError}
            onRetry={reloadAccounts}
            columns={[
              {
                header: "Name",
                sortable: true,
                sortKey: "name",
                accessor: (account) => account.name,
              },
              {
                header: "Code",
                sortable: true,
                sortKey: "code",
                accessor: (account) => account.code,
              },
              {
                header: "Category",
                accessor: (account) => (
                  <span className="inline-flex rounded-full px-2 text-xs font-semibold leading-5 bg-blue-100 text-blue-800">
                    {t(convertToTitleCase(account.category))}
                  </span>
                ),
              },
              {
                header: "Type",
                accessor: (account) => t(convertToTitleCase(account.type)),
                className: "text-gray-500",
              },
              {
                header: "accounts.fields.balance",
                accessor: (account) => {
                  if (accountBalancesLoading) {
                    return (
                      <div className="h-4 w-24 bg-gray-100 rounded animate-pulse" />
                    );
                  }
                  const balanceData = accountBalances[account.id];
                  if (!balanceData) {
                    return <span className="text-gray-400">-</span>;
                  }
                  return (
                    <span
                      className={
                        balanceData.balance >= 0
                          ? "text-green-600 font-medium"
                          : "text-red-600 font-medium"
                      }
                    >
                      {formatCurrency(balanceData.balance)}
                    </span>
                  );
                },
                align: "right",
              },
              {
                header: "common.actions.label",
                accessor: (account, index) => (
                  <TableActions
                    position={index === accounts.length - 1 ? "top" : "bottom"}
                    actions={[
                      {
                        label: t("common.viewTransactions"),
                        onClick: () =>
                          navigate(
                            `/v2/accounting/transactions?accountId=${account.id}`,
                          ),
                      },
                      {
                        label: t("common.edit"),
                        onClick: () => handleEdit(account),
                      },
                      {
                        label: t("common.delete"),
                        onClick: () => handleRemove(account),
                        variant: "danger",
                      },
                    ]}
                  />
                ),
                align: "right",
              },
            ]}
            data={displayedAccounts}
          />
        </div>

        {/* Pagination */}
        <Pagination
          nextTokens={accountNextTokens}
          currentPage={page}
          isLoading={accountLoading}
          perPage={perPage}
          onPageChange={onPageChange}
          onPerPageChange={onPerPageChange}
        />

        {/* Account Modal */}
        {accountModalOpen && (
          <ChartOfAccountModal
            open={accountModalOpen}
            onClose={handleClose}
            account={selectedAccount}
            initialValues={initialAccountValues}
          />
        )}

        {/* Upload Account Modal */}
        {isAccountUploadModalOpen && (
          <AccountUploadModal
            open={isAccountUploadModalOpen}
            onClose={handleClose}
          />
        )}

        {/* Delete Modal */}
        {showConfirmModal && (
          <ModalAlert
            open={showConfirmModal}
            setOpen={(open) => {
              setShowConfirmModal(open);
            }}
            title="Delete Account"
            description="accounts.modal.description.delete"
            descriptionNode={getDeleteDescription()}
            confirmButtonText="Yes"
            cancelButtonText="Cancel"
            loadingButton={deleteLoading}
            disabledButton={deleteLoading}
            onConfirm={onDelete}
            onCancel={() => {
              cancelAPIRequest(request);
              setShowConfirmModal(false);
              setSelectedAccount(null);
            }}
          />
        )}

        {/* Error Modal */}
        {deleteError && (
          <ErrorDialog
            open={showErrorModal}
            error={deleteError}
            onClose={() => {
              setShowErrorModal(false);
              clearAcctAccountError(dispatch);
            }}
          />
        )}
      </main>
    </AccountingWrapper>
  );
};

export default ChartOfAccounts;
